import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import * as styles from './ScanDemo.module.scss';
import { Reader } from '../../main/component/code-scanner/barcodeReader/Reader';
import { BarcodeDetector } from '../../main/component/code-scanner/barcodeReader/BarcodeDetector';

export const ScanDemo = () => {
    const [cameras, setCameras] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState('');
    const [selectedScanner, setSelectedScanner] = useState('zxing');
    const [scanResult, setScanResult] = useState(null);
    const [cameraPermission, setCameraPermission] = useState('prompt'); // 'prompt', 'granted', 'denied'
    const [cameraActive, setCameraActive] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    const scanners = [
        { id: 'zxing', label: 'Текущий (zxing-wasm)' },
        { id: 'barcodeDetector', label: 'BarcodeDetector' }
    ];

    const onScan = (v) => {
        const result = v?.result?.text || 'не распознан';
        console.log(result);

        setScanResult(result);

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            setScanResult(null);
        }, 5000);
    };

    // Запрос разрешения на камеру (по кнопке)
    const requestCameraPermission = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(track => track.stop()); // Сразу останавливаем, просто получили разрешение
            setCameraPermission('granted');

            // После получения разрешения обновляем список камер
            await getCameras();
            return true;
        } catch (error) {
            console.error('Ошибка получения разрешения:', error);
            setCameraPermission('denied');
            alert('Нет доступа к камере. Пожалуйста, разрешите доступ в настройках браузера.');
            return false;
        }
    };

    // Получение списка камер
    const getCameras = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setCameras(videoDevices);
            if (videoDevices.length > 0 && !selectedCamera) {
                setSelectedCamera(videoDevices[0].deviceId);
            }
            return videoDevices;
        } catch (error) {
            console.error('Ошибка получения списка камер:', error);
            return [];
        }
    };

    // Запуск камеры с выбранным устройством
    const startCamera = async () => {
        if (!selectedCamera) return;

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: selectedCamera } }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
            streamRef.current = stream;
            setCameraActive(true);
        } catch (error) {
            console.error('Ошибка запуска камеры:', error);
            setCameraActive(false);

            // Если ошибка из-за отсутствия разрешения
            if (error.name === 'NotAllowedError') {
                setCameraPermission('denied');
            }
        }
    };

    // Остановка камеры
    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraActive(false);
    };

    // Инициализация: проверяем разрешение и получаем камеры
    useEffect(() => {
        const init = async () => {
            // Проверяем, есть ли уже разрешение
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasVideoDevices = devices.some(device => device.kind === 'videoinput');

            if (hasVideoDevices && devices.some(d => d.label)) {
                // Если есть метки камер - разрешение уже дано
                setCameraPermission('granted');
                await getCameras();
            }
        };

        init();

        // Слушаем изменения устройств
        navigator.mediaDevices.addEventListener('devicechange', getCameras);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', getCameras);
            stopCamera();
        };
    }, []);

    // Запускаем камеру когда выбрана камера и есть разрешение
    useEffect(() => {
        if (cameraPermission === 'granted' && selectedCamera) {
            startCamera();
        }

        return () => {
            stopCamera();
        };
    }, [selectedCamera, cameraPermission]);

    // Очистка таймера
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const handleCameraChange = (event) => {
        setSelectedCamera(event.target.value);
    };

    const handleScannerChange = (event) => {
        setSelectedScanner(event.target.value);
    };

    return <div className={styles.wrapper}>
        <h2>Scaner Demo</h2>

        {cameraPermission !== 'granted' && (
            <div className={styles.permission}>
                <button onClick={requestCameraPermission}>
                    Разрешить доступ к камере
                </button>
                <p>
                    Для сканирования кодов необходим доступ к камере
                </p>
            </div>
        )}

        {cameraPermission === 'granted' && (
            <>
                <div className={styles.options}>
                    <select value={selectedCamera} onChange={handleCameraChange}>
                        {cameras.map(camera => (
                            <option key={camera.deviceId} value={camera.deviceId}>
                                {camera.label || `Камера ${camera.deviceId.slice(0, 8)}`}
                            </option>
                        ))}
                    </select>

                    <select value={selectedScanner} onChange={handleScannerChange}>
                        {scanners.map(scanner => (
                            <option key={scanner.id} value={scanner.id}>
                                {scanner.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.result}>
                    {scanResult
                        ? <p>Результат: <br />{scanResult}</p>
                        : <p>отсканируйте код</p>
                    }
                </div>

                {selectedScanner == 'zxing' && cameraActive && <Reader
                    deviceId={selectedCamera}
                    onScan={onScan} />}

                {selectedScanner == 'barcodeDetector' && cameraActive && <BarcodeDetector deviceId={selectedCamera}
                    onScan={onScan} />}
            </>
        )}
    </div>
};